import { axios_instance } from "@/api/api";

interface CreateCampaignRequest {
  name: string;
  description: string;
}

interface CreateCampaignResponse {
  id: string;
  name: string;
  description: string;
  createdAt: string;
}

export async function createCampaign(
  payload: CreateCampaignRequest,
): Promise<CreateCampaignResponse> {
  try {
    const response = await axios_instance.post<CreateCampaignResponse>(
      "/api/v1/view/campaign",
      payload,
    );
    return response.data;
  } catch (error) {
    console.error("Failed to create campaign:", error);
    throw error;
  }
}

export async function getCampaignsPagination(
  payload: Record<string, unknown>,
): Promise<CreateCampaignResponse[] | null> {
  try {
    const response = await axios_instance.post<{
      data: CreateCampaignResponse[];
    }>("/api/v1/view/campaign/pagination", payload);
    return response.data.data;
  } catch (error) {
    console.error("Failed to fetch campaigns pagination:", error);
    return null;
  }
}

export async function getCampaignsPaginationFull(
  payload: Record<string, unknown>,
): Promise<{
  data: CreateCampaignResponse[] | null;
  total_filter: number;
} | null> {
  try {
    const response = await axios_instance.post<{
      data: CreateCampaignResponse[] | null;
      total_filter: number;
    }>("/api/v1/view/campaign/pagination", payload);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch campaigns pagination (full):", error);
    return null;
  }
}

export async function getCampaignById(
  campaignId: string,
): Promise<{ data: CreateCampaignResponse } | undefined> {
  try {
    const response = await axios_instance.get<{ data: CreateCampaignResponse }>(
      `/api/v1/view/campaign/${campaignId}`,
    );
    return response.data;
  } catch (error) {
    console.error("Failed to fetch campaign:", error);
  }
}

export async function deleteCampaign(campaignId: string): Promise<boolean> {
  try {
    await axios_instance.delete(`/api/v1/view/campaign/${campaignId}`);
    return true;
  } catch (error) {
    console.error("Failed to delete campaign:", error);
    return false;
  }
}

export async function updateCampaign(
  campaignId: string,
  payload: { name: string; description?: string },
): Promise<{ message: string }> {
  try {
    const response = await axios_instance.post(
      `/api/v1/view/campaign/${campaignId}`,
      payload,
    );
    return response.data;
  } catch (error) {
    console.error("Failed to update campaign:", error);
    throw error;
  }
}

interface GetCampaignStatResponse {
  data: Array<{ _id: string; count: number }>;
  message: string;
}

export async function getCampaignStat(): Promise<GetCampaignStatResponse | null> {
  try {
    const response = await axios_instance.get<GetCampaignStatResponse>(
      "/api/v1/view/campaign/stat",
    );
    return response.data;
  } catch (error) {
    console.error("Failed to fetch campaign stat:", error);
    return null;
  }
}

export interface VersionAgentItem {
  id: string;
  name: string;
  campaign: string;
  path: string;
  type: string[];
  md5_dll: string;
  server_1_host: string;
  server_2_host: string;
  server_1_port: number;
  server_2_port: number;
  created_at: number;
}

interface VersionAgentPaginationResponse {
  data: VersionAgentItem[] | null;
  message: string;
  total_filter: number;
}

interface VersionAgentPaginationRequest {
  time_from?: number;
  time_to?: number;
  keyword?: string;
  page: number;
  size: number;
  campaign: string;
}

export async function getVersionAgentPagination(
  payload: VersionAgentPaginationRequest,
): Promise<VersionAgentPaginationResponse | null> {
  try {
    const response = await axios_instance.post<VersionAgentPaginationResponse>(
      "/api/v1/view/version-agent/pagination",
      payload,
    );
    return response.data;
  } catch (error) {
    console.error("Failed to fetch version agent pagination:", error);
    return null;
  }
}

interface CreateCampaignInstallerRequest {
  name: string;
  campaign: string;
  type: string[];
  md5_dll: string;
  server_1_host: string;
  server_1_port: number;
  server_2_host: string;
  server_2_port: number;
}

interface CreateCampaignInstallerResponse {
  message: string;
}

export async function createCampaignInstaller(
  payload: CreateCampaignInstallerRequest,
): Promise<CreateCampaignInstallerResponse> {
  try {
    const response = await axios_instance.post<CreateCampaignInstallerResponse>(
      "/api/v1/view/version-agent/",
      payload,
    );
    return response.data;
  } catch (error) {
    console.error("Failed to create version agent:", error);
    throw error;
  }
}

export async function deleteVersionAgent(
  versionAgentId: string,
): Promise<boolean> {
  try {
    await axios_instance.delete(`/api/v1/view/version-agent/${versionAgentId}`);
    return true;
  } catch (error) {
    console.error("Failed to delete version agent:", error);
    return false;
  }
}

export interface CampaignActivityByType {
  type: string;
  count: number;
}

export interface CampaignActivityTrendItem {
  day: string;
  total: number;
  by_type: CampaignActivityByType[];
}

interface CampaignActivityResponse {
  data: {
    total_actions: number;
    screenshots: number;
    by_type: Record<string, number>;
    trend: CampaignActivityTrendItem[];
  } | null;
  message: string;
}

export async function fetchCampaignActivity(
  campaignId: string,
  days?: number,
): Promise<CampaignActivityResponse | null> {
  try {
    const query = days ? `?days=${days}` : "";
    const response = await axios_instance.get<CampaignActivityResponse>(
      `/api/v1/view/campaign/activity/${campaignId}${query}`,
    );
    return response.data;
  } catch (error) {
    console.error("Failed to fetch campaign activity:", error);
    return null;
  }
}
